import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  subscribeToDeal, getPIFSubmissions, getAnalystReviews,
  getIICApprovals, getDealAuditLog, getDealDocuments, assignAnalyst,
} from '../../services/dealService';
import { getFundById, getAllRubrics, getAllCountries } from '../../services/firestoreService';
import { generateReopenToken } from '../../services/pifService';
import { getDealSLAInfo, getSLAConfigsForFund } from '../../services/slaService';
import { formulaShortLabel } from '../../config/formulaTypes';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import StageProgressBar from '../../components/pipeline/StageProgressBar';
import PIFScoreCard from '../../components/pipeline/PIFScoreCard';
import AISummaryPanel from '../../components/pipeline/AISummaryPanel';
import FlagPanel from '../../components/pipeline/FlagPanel';
import AuditLogPanel from '../../components/pipeline/AuditLogPanel';
import DataroomPanel from '../../components/pipeline/DataroomPanel';
import FrameworksPanel from '../../components/pipeline/FrameworksPanel';
import TermSheetPanel from '../../components/pipeline/TermSheetPanel';
import ECIMPanel from '../../components/pipeline/ECIMPanel';
import SLABadge from '../../components/pipeline/SLABadge';
import Badge from '../../components/shared/UI/Badge';
import Button from '../../components/shared/UI/Button';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';
import Modal from '../../components/shared/UI/Modal';
import { formatDateTime, formatCurrency, formatShortId, formatDate, resolveUserName, formatFigureValue } from '../../utils/formatters';
import { db } from '../../config/firebase';
import { doc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { writeDealAuditLog } from '../../services/auditService';
import UsdCagrNarration from '../../components/pipeline/UsdCagrNarration';
import FutureUsdCagrNarration from '../../components/pipeline/FutureUsdCagrNarration';

export default function DealRecordPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [deal, setDeal] = useState(null);

  const [rubric, setRubric] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [slaInfo, setSlaInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [reopenModal, setReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenSuccess, setReopenSuccess] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dealReopenConfirm, setDealReopenConfirm] = useState(false);
  const [dealReopenLoading, setDealReopenLoading] = useState(false);
  const [countryName, setCountryName] = useState(null);
  const [isInitiating, setIsInitiating] = useState(false);
  const [initError, setInitError] = useState('');
  const [initSuccess, setInitSuccess] = useState('');
  const [dsqResponses, setDsqResponses] = useState([]);
  // The DSQ template supplies the display labels (section titles, question text,
  // option text). Response docs store ids only, so the tab joins against it.
  const [dsqTemplate, setDsqTemplate] = useState(null);
  // Resolved display names for legacy records that pre-date name denormalisation
  const [analystDisplayName, setAnalystDisplayName] = useState(null);
  const [peerReviewerDisplayName, setPeerReviewerDisplayName] = useState(null);

  // Real-time deal subscription
  useEffect(() => {
    const unsub = subscribeToDeal(dealId, setDeal);
    return () => unsub();
  }, [dealId]);

  // Load supporting data
  useEffect(() => {
    if (!deal) return;

    async function loadData() {
      const [, subs, revs, apps, log, docs, allRubrics] = await Promise.all([
        getFundById(deal.fund_id),
        getPIFSubmissions(dealId),
        getAnalystReviews(dealId),
        getIICApprovals(dealId),
        getDealAuditLog(dealId),
        getDealDocuments(dealId),
        getAllRubrics(),
      ]);

      setSubmissions(subs);
      setReviews(revs);
      setApprovals(apps);
      setAuditLog(log);
      setDocuments(docs);

      const pub = allRubrics.find(r =>
        r.fund_id === deal.fund_id &&
        (deal.stream_id ? r.stream_id === deal.stream_id : !r.stream_id) &&
        r.is_published
      );
      setRubric(pub);

      const slaConfigs = await getSLAConfigsForFund(deal.fund_id);
      setSlaInfo(getDealSLAInfo(deal, slaConfigs));

      // Look up full country name from ISO code
      if (deal.company_country) {
        getAllCountries().then(countries => {
          const c = countries.find(cn => cn.id === deal.company_country);
          setCountryName(c?.name || deal.company_country);
        }).catch(() => setCountryName(deal.company_country));
      }

      // Resolve analyst/reviewer names for legacy records missing the _name field
      if (deal.assigned_analyst_id && !deal.assigned_analyst_name) {
        resolveUserName(deal.assigned_analyst_id, db).then(setAnalystDisplayName);
      }
      if (deal.peer_reviewer_id && !deal.peer_reviewer_name) {
        resolveUserName(deal.peer_reviewer_id, db).then(setPeerReviewerDisplayName);
      }

      setLoading(false);
    }

    loadData();
  }, [deal, dealId, refreshKey]);

  async function handleAssignToMe() {
    setAssigning(true);
    try {
      await assignAnalyst({
        dealId,
        analystId: user?.uid,
        analystName: userProfile?.full_name || userProfile?.display_name || null,
        performedBy: user?.uid,
        performedByName: userProfile?.full_name || userProfile?.display_name || null,
      });
    } finally {
      setAssigning(false);
    }
  }

  async function handleReopenDeal() {
    setDealReopenLoading(true);
    try {
      const performedByName = userProfile?.full_name || userProfile?.display_name || user?.email || '';
      await updateDoc(doc(db, 'deals', dealId), {
        status: 'active',
        current_stage: 'analyst_review',
        reopened_at: new Date().toISOString(),
        reopened_by: user?.uid,
        reopened_by_name: performedByName,
        suspension_reason: null,
        suspended_at: null,
      });
      await writeDealAuditLog({
        dealId,
        action: 'deal_reopened',
        performedBy: user?.uid,
        performedByName,
        details: { previous_status: 'suspended', returned_to_stage: 'analyst_review' },
        stageAtTime: deal.current_stage,
      });
      setDealReopenConfirm(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setDealReopenLoading(false);
    }
  }

  async function handleReopen() {
    if (!reopenReason.trim()) return;
    setReopenLoading(true);
    try {
      await generateReopenToken({ dealId, reason: reopenReason });
      setReopenSuccess(true);
      setTimeout(() => { setReopenModal(false); setReopenSuccess(false); setReopenReason(''); }, 2500);
    } catch (e) {
      alert(e.message);
    } finally {
      setReopenLoading(false);
    }
  }

  async function handleInitiateDSQ() {
    if (!window.confirm(`Send DSQ invitation to ${deal.company_email || deal.contact_email}?`)) return;
    setIsInitiating(true);
    setInitError('');
    setInitSuccess('');
    try {
      const fns = getFunctions();
      const invite = httpsCallable(fns, 'inviteCompanyToScreening');
      const result = await invite({ dealId });
      setInitSuccess(result.data.message);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setInitError(err.message || 'Failed to send invitation. Please try again.');
    } finally {
      setIsInitiating(false);
    }
  }

  async function loadDsqResponses() {
    try {
      const q = query(
        collection(db, 'deals', dealId, 'ddq_responses'),
        where('template_type', '==', 'dsq')
      );
      const snap = await getDocs(q);
      setDsqResponses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      // Non-fatal
    }

    // Join target for the labels. Same query shape the rest of the DSQ code uses
    // (CompanyDSQPage): stream_id passes through ?? null because Firestore's
    // `== null` matches an explicitly-null field only, never an absent one.
    try {
      const tmplQ = query(
        collection(db, 'ddq_templates'),
        where('fund_id', '==', deal.fund_id),
        where('stream_id', '==', deal.stream_id ?? null),
        where('template_type', '==', 'dsq'),
        where('status', '==', 'published'),
        limit(1)
      );
      const tmplSnap = await getDocs(tmplQ);
      setDsqTemplate(tmplSnap.empty ? null : { id: tmplSnap.docs[0].id, ...tmplSnap.docs[0].data() });
    } catch {
      // Non-fatal — labels fall back to raw ids.
    }
  }

  // Label resolvers. A template can be unpublished or edited after a company has
  // answered, so every lookup is optional and the caller falls back to the raw id.
  function findDsqSection(sectionId) {
    return (dsqTemplate?.sections || []).find(s => s.section_id === sectionId) || null;
  }

  function findDsqQuestion(sectionId, questionId) {
    return (findDsqSection(sectionId)?.questions || []).find(q => q.question_id === questionId) || null;
  }

  if (loading && !deal) return <PortalLayout><LoadingSpinner /></PortalLayout>;
  if (!deal) return <PortalLayout><div className="alert alert-error">Deal not found.</div></PortalLayout>;

  const currentSubmission = submissions.find(s => s.is_current) || submissions[0];

  const dealUserRoles = userProfile?.roles || (userProfile?.role ? [userProfile.role] : []);
  const canAssign = !deal.assigned_analyst_id &&
    (dealUserRoles.includes('analyst') || dealUserRoles.includes('head_of_pipeline')) &&
    (userProfile?.fund_access || []).includes(deal.fund_id);

  const isAssignedAnalyst = deal.assigned_analyst_id === user?.uid;
  const isPeerReviewer = deal.peer_reviewer_id === user?.uid;
  const isHoP = dealUserRoles.includes('head_of_pipeline');
  const isPrincipal = ['head_of_pipeline', 'cio', 'md'].some(r => dealUserRoles.includes(r));

  const canInitiateDSQ = dealUserRoles.some(r => ['analyst', 'head_of_pipeline', 'admin'].includes(r));

  const isInternalUser = dealUserRoles.some((r) =>
    ['analyst', 'head_of_pipeline', 'cio', 'md', 'admin'].includes(r),
  );

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'pif', label: 'PIF Submission' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'approvals', label: 'Approvals' },
    ...(deal.dsq_initiated ? [{ id: 'dsq', label: 'DSQ', onClick: loadDsqResponses }] : []),
    { id: 'documents', label: 'Documents' },
    ...(isInternalUser ? [{ id: 'frameworks', label: 'Frameworks' }] : []),
    ...(isInternalUser ? [{ id: 'ecim', label: 'e-CIM' }] : []),
    ...(isInternalUser ? [{ id: 'termsheet', label: 'Term Sheet' }] : []),
    { id: 'audit', label: 'Audit Log' },
  ];

  return (
    <PortalLayout>
      {/* Back + Header */}
      <div style={{ marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/pipeline')}>← Pipeline</Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)' }}>{deal.company_name}</h1>
            <Badge text={deal.status || 'active'} />
          </div>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Ref: {formatShortId(deal.id)} &nbsp;·&nbsp;
            {deal.sector} &nbsp;·&nbsp;
            {countryName || deal.company_country} &nbsp;·&nbsp;
            {formatCurrency(deal.requested_amount, deal.requested_currency)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {slaInfo && <SLABadge {...slaInfo} />}
          {isAssignedAnalyst && deal.current_stage === 'analyst_review' && (
            <Button size="sm" variant="primary" onClick={() => navigate(`/pipeline/${dealId}/review`)}>
              Complete Review
            </Button>
          )}
          {isPeerReviewer && deal.current_stage === 'peer_review' && (
            <Button size="sm" variant="primary" onClick={() => navigate(`/pipeline/${dealId}/peer-review`)}>
              Peer Review
            </Button>
          )}
          {isHoP && deal.current_stage === 'hop_review' && (
            <Button size="sm" variant="primary" onClick={() => navigate(`/pipeline/${dealId}/hop-review`)}>
              HoP Review
            </Button>
          )}
          {isPrincipal && (deal.current_stage === 'iic_approval' || deal.current_stage === 'iic_approval_ic1') && (
            <Button size="sm" variant="primary" onClick={() => navigate(`/pipeline/${dealId}/iic-approval`)}>
              IIC Approval
            </Button>
          )}
        </div>
      </div>

      {/* Stage Progress */}
      <StageProgressBar currentStage={deal.current_stage} />

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); if (t.onClick) t.onClick(); }} style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: activeTab === t.id ? 600 : 400,
            color: activeTab === t.id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            borderBottom: activeTab === t.id ? '2px solid var(--color-accent)' : '2px solid transparent',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'row', gap: 20, alignItems: 'flex-start' }}>
          {/* Left panel — 25% */}
          <div style={{ flex: '0 0 25%', minWidth: 0 }}>
            {/* Company Details */}
            <div className="card mb-4">
              <div className="card-header"><h2>Company Details</h2></div>
              <div className="card-body">
                {[
                  ['Company', deal.company_name],
                  ['Country', countryName || deal.company_country],
                  ['Sector', deal.sector],
                  ['Sub-sector', deal.sub_sector],
                  ['Contact', `${deal.contact_name} — ${deal.contact_title}`],
                  ['Email', deal.company_email],
                  ['Requested', formatCurrency(deal.requested_amount, deal.requested_currency)],
                  ['Submitted', formatDate(deal.pif_submitted_at)],
                  ['Stage entered', formatDate(deal.stage_entered_at)],
                ].map(([label, value]) => value ? (
                  <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', minWidth: 80 }}>{label}</p>
                    <p style={{ fontSize: 13 }}>{value}</p>
                  </div>
                ) : null)}
              </div>
            </div>

            {/* Assignment */}
            <div className="card mb-4">
              <div className="card-header"><h2>Assignments</h2></div>
              <div className="card-body">
                <div style={{ marginBottom: 12 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>Primary Analyst</p>
                  {deal.assigned_analyst_id
                    ? <p style={{ fontSize: 14 }}>
                        {deal.assigned_analyst_name
                          ? deal.assigned_analyst_name
                          : analystDisplayName
                            ? analystDisplayName
                            : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Loading…</span>}
                      </p>
                    : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 14, color: 'var(--color-error)', fontWeight: 600 }}>Unassigned</span>
                        {canAssign && (
                          <Button size="sm" variant="primary" loading={assigning} onClick={handleAssignToMe}>
                            Assign to Me
                          </Button>
                        )}
                      </div>
                    )
                  }
                </div>
                {deal.peer_reviewer_id && (
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>Peer Reviewer</p>
                    <p style={{ fontSize: 14 }}>
                      {deal.peer_reviewer_name
                        ? deal.peer_reviewer_name
                        : peerReviewerDisplayName
                          ? peerReviewerDisplayName
                          : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Loading…</span>}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* PIF Score */}
            <PIFScoreCard submission={currentSubmission} rubric={rubric} />

            {/* Reopen suspended deal */}
            {deal.status === 'suspended' && (dealUserRoles.includes('admin') || dealUserRoles.includes('head_of_pipeline')) && (
              <div className="card mt-4">
                <div className="card-header"><h2>Deal Suspended</h2></div>
                <div className="card-body">
                  {deal.suspension_reason && (
                    <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                      Reason: {deal.suspension_reason}
                    </p>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => setDealReopenConfirm(true)}>
                    Reopen Deal
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right panel — 75% */}
          <div style={{ flex: '0 0 75%', minWidth: 0, overflowX: 'auto' }}>
            {/* DD Approval Details — shown once advanced to due diligence */}
            {deal.current_stage === 'due_diligence' && (deal.dd_budget_approved != null || deal.dd_mandate_notes) && (
              <div className="card mb-4" style={{ borderLeft: '4px solid var(--color-success)' }}>
                <div className="card-header"><h2>DD Approval Details</h2></div>
                <div className="card-body">
                  <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', minWidth: 120 }}>DD Budget</p>
                    <p style={{ fontSize: 14, fontWeight: 700 }}>
                      {deal.dd_budget_approved != null
                        ? formatCurrency(deal.dd_budget_approved, deal.requested_currency)
                        : '—'}
                    </p>
                  </div>
                  {deal.dd_mandate_notes && (
                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', minWidth: 120 }}>DD Mandate Notes</p>
                      <p style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{deal.dd_mandate_notes}</p>
                    </div>
                  )}
                  {deal.advanced_to_dd_by_name && (
                    <div style={{ display: 'flex', gap: 12 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', minWidth: 120 }}>Approved by</p>
                      <p style={{ fontSize: 13 }}>
                        {deal.advanced_to_dd_by_name}
                        {deal.advanced_to_dd_at && ` on ${formatDate(deal.advanced_to_dd_at)}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AI Summary */}
            {currentSubmission && (
              <div className="mb-4">
                <AISummaryPanel
                  submission={currentSubmission}
                  dealId={dealId}
                  onConfirmed={() => setRefreshKey(k => k + 1)}
                />
              </div>
            )}

            {/* DSQ Initiation Card — screening stage */}
            {deal.current_stage === 'screening' && (
              <div className="card mb-4" style={{ borderLeft: '4px solid #1e3a5f' }}>
                <div className="card-header"><h2>Deal Screening Questionnaire</h2></div>
                <div className="card-body">
                  {!deal.dsq_initiated ? (
                    <>
                      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
                        This deal has advanced to screening. Initiate the DSQ process to invite the company to complete their screening questionnaire.
                      </p>
                      {initSuccess && <div className="alert alert-success mb-3">{initSuccess}</div>}
                      {initError && <div className="alert alert-error mb-3">{initError}</div>}
                      {canInitiateDSQ && (
                        <Button variant="primary" loading={isInitiating} onClick={handleInitiateDSQ}>
                          Initiate DSQ
                        </Button>
                      )}
                    </>
                  ) : (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 8 }}>
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>Invited</p>
                          <p style={{ fontSize: 14 }}>{deal.company_email}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>Sent</p>
                          <p style={{ fontSize: 14 }}>{formatDate(deal.dsq_initiated_at)} by {deal.dsq_initiated_by_name || '—'}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>Status</p>
                          {deal.dsq_company_submitted
                            ? <span className="badge badge-success">Submitted</span>
                            : <span className="badge badge-warning">Pending</span>
                          }
                        </div>
                        {deal.dsq_score != null && (
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>DSQ Score</p>
                            <p style={{ fontSize: 14, fontWeight: 700 }}>{deal.dsq_score?.toFixed(2)} / {deal.dsq_max_possible_sum?.toFixed(2) || '10'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Risk Flags */}
            <FlagPanel reviews={reviews} />
          </div>
        </div>
      )}

      {/* ── PIF SUBMISSION TAB ── */}
      {activeTab === 'pif' && (
        <div>
          {submissions.length === 0 && (
            <div className="alert alert-info">No PIF submissions yet.</div>
          )}
          {submissions.map((sub, idx) => (
            <div key={sub.id} className="card mb-4">
              <div className="card-header">
                <h2>Submission #{sub.submission_number || idx + 1}{sub.is_current ? ' (Current)' : ''}</h2>
                <p className="text-muted" style={{ fontSize: 13 }}>{formatDateTime(sub.submitted_at)}</p>
              </div>
              <div className="card-body">
                {/* Fix 2 Stage B — currency normalisation banner */}
                {sub.currency_normalisation_applied && (
                  <div className="alert alert-info" style={{ marginBottom: 16 }}>
                    Currency normalisation applied. Monetary figures were submitted in{' '}
                    <strong>{sub.reporting_currency}</strong> and converted to each question's
                    scoring currency before evaluation. Original values are shown alongside
                    converted values below.
                    {sub.exchange_rate_from_cache && sub.exchange_rates_at_submission?.fetched_at && (
                      <> {' '}(Exchange rates from cache dated{' '}
                        {formatDate(sub.exchange_rates_at_submission.fetched_at)} — live rates
                        were unavailable at submission time)</>
                    )}
                  </div>
                )}

                {/* Score breakdown */}
                <div style={{ marginBottom: 16, padding: 14, background: 'var(--color-border-light)', borderRadius: 'var(--radius)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 8 }}>
                    {[
                      ['Raw Score', sub.raw_score?.toFixed(2)],
                      ['Normalised', sub.normalised_score?.toFixed(2)],
                      ['Threshold', rubric?.advancement_threshold],
                      ['Outcome', sub.meets_threshold ? '✓ Passes' : '✗ Below threshold'],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--color-text-secondary)', fontWeight: 600 }}>{label}</p>
                        <p style={{ fontSize: 16, fontWeight: 700, color: label === 'Outcome' ? (sub.meets_threshold ? 'var(--color-success)' : 'var(--color-error)') : 'var(--color-text)' }}>{value ?? '—'}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Question responses */}
                {(sub.responses || []).map(r => {
                  const question = rubric?.questions?.find(q => q.question_id === r.question_id);
                  if (!question) return null;

                  // Derived question — show computed value and source breakdown
                  if (r.response_type === 'derived') {
                    return (
                      <div key={r.question_id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--color-border-light)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <p style={{ fontSize: 13, fontWeight: 600 }}>{question.question_text}</p>
                          <span style={{
                            fontSize: 10, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                            background: 'rgba(201,149,42,0.12)', color: '#c9952a',
                          }}>
                            {/* Friendly name, not the raw code: the two CAGRs
                                format identically, so this badge is what tells
                                a reader which metric they are looking at. */}
                            {formulaShortLabel(r.formula_type)}
                          </span>
                        </div>
                        {r.computation_error ? (
                          <div style={{
                            padding: '8px 12px', borderRadius: 6, fontSize: 13,
                            background: 'rgba(201,149,42,0.08)', border: '1px solid rgba(201,149,42,0.3)',
                          }}>
                            Could not compute: {r.computation_error} — Score for this question: 0
                          </div>
                        ) : (
                          <>
                            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
                              {r.computed_value_formatted ?? '—'}
                            </p>
                            {/* CAGR_USD is checked FIRST and deliberately. It
                                sets oldest_year, so it would otherwise fall
                                into the branch below — which reads
                                oldest_value/newest_value, fields CAGR_USD does
                                not have (it stores oldest_value_usd /
                                newest_value_usd), rendering "(undefined)". */}
                            {r.source_values && (
                              r.formula_type === 'CAGR_USD_FUTURE'
                                ? <FutureUsdCagrNarration sv={r.source_values} />
                                : r.formula_type === 'CAGR_USD' && Array.isArray(r.source_values.years_used)
                                ? <UsdCagrNarration sv={r.source_values} />
                                : (
                                  <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                                    {r.source_values.oldest_year
                                      ? `Based on: ${r.source_values.oldest_year} (${r.source_values.oldest_value?.toLocaleString()}) → ${r.source_values.newest_year} (${r.source_values.newest_value?.toLocaleString()}) over ${r.source_values.actual_periods} years`
                                      : `Inputs: ${Object.entries(r.source_values).map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toLocaleString() : v}`).join(', ')}`
                                    }
                                  </p>
                                )
                            )}
                          </>
                        )}
                        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          Score: {r.score_awarded} × weight {question.weight} = <strong>{r.weighted_score?.toFixed(2)}</strong>
                        </p>
                      </div>
                    );
                  }

                  // Standard direct question
                  // Fix 2 Stage B — dual currency display for converted monetary figures
                  const isConverted = r.conversion_applied === true &&
                    r.original_value != null && r.converted_value != null;
                  return (
                    <div key={r.question_id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--color-border-light)' }}>
                      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{question.question_text}</p>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {isConverted ? (
                          <div>
                            <p style={{ fontSize: 13 }}>
                              Answer: <strong>{r.original_currency} {Number(r.original_value).toLocaleString()}</strong>
                            </p>
                            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                              ({r.converted_currency} {Math.round(Number(r.converted_value)).toLocaleString()} at rate {r.conversion_rate} on {formatDate(sub.submitted_at)})
                            </p>
                          </div>
                        ) : (
                          <p style={{ fontSize: 13 }}>
                            Answer: <strong>
                              {question.response_type === 'figure'
                                ? `${formatFigureValue(r.response_value)} ${question.figure_unit || ''}`
                                : question.options?.find(o => o.option_id === r.selected_option_id)?.option_text || r.response_value || '—'
                              }
                            </strong>
                          </p>
                        )}
                        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          Score: {r.score_awarded} × weight {question.weight} = <strong>{r.weighted_score?.toFixed(2)}</strong>
                        </p>
                      </div>
                    </div>
                  );
                })}

                {/* Reopen button */}
                {sub.is_current && (isAssignedAnalyst || isPrincipal) && (
                  <div style={{ marginTop: 16 }}>
                    <Button size="sm" variant="secondary" onClick={() => setReopenModal(true)}>
                      Reopen PIF for Updates
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Documents */}
          {documents.length > 0 && (
            <div className="card">
              <div className="card-header"><h2>Uploaded Documents</h2></div>
              <div className="card-body">
                {documents.map(docItem => (
                  <div key={docItem.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-border-light)' }}>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 500 }}>{docItem.file_name}</p>
                      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{(docItem.file_size_bytes / 1024 / 1024).toFixed(1)} MB</p>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{formatDate(docItem.uploaded_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── REVIEWS TAB ── */}
      {activeTab === 'reviews' && (
        <div>
          {reviews.length === 0 ? (
            <div className="card">
              <div className="card-body">
                <p className="text-muted" style={{ fontSize: 13 }}>No reviews have been submitted yet.</p>
              </div>
            </div>
          ) : (
            // Show ALL reviews in chronological order (oldest first = natural reading order)
            // getAnalystReviews already orders by created_at asc
            reviews.map((review, idx) => {
              const REVIEW_LABELS = {
                primary_analyst: 'Analyst Review',
                peer_review: 'Peer Review',
                hop_review: 'Head of Pipeline Review',
              };
              const label = REVIEW_LABELS[review.review_type] || review.review_type?.replace(/_/g, ' ');
              const isNegative = review.outcome === 'decline' || review.outcome === 'send_back' || review.outcome === 'deny';
              return (
                <div key={review.id || idx} className="card mb-4">
                  <div className="card-header">
                    <div>
                      <h2>{label}</h2>
                      <p className="text-muted" style={{ fontSize: 13 }}>{formatDateTime(review.completed_at || review.created_at)}</p>
                    </div>
                  </div>
                  <div className="card-body">
                    {review.notes && (
                      <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 12 }}>{review.notes}</p>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: review.outcome ? 8 : 0 }}>
                      {review.sector_concentration_flag && <span className="badge badge-warning">⚑ Sector Risk</span>}
                      {review.obligor_exposure_flag && <span className="badge badge-warning">⚑ Obligor Exposure</span>}
                      {review.dry_powder_flag && <span className="badge badge-warning">⚑ Dry Powder</span>}
                    </div>
                    {review.outcome && (
                      <p style={{ fontSize: 13, marginTop: 4 }}>
                        Outcome: <strong style={{ color: isNegative ? 'var(--color-error)' : 'var(--color-success)' }}>
                          {review.outcome.replace(/_/g, ' ')}
                        </strong>
                      </p>
                    )}
                    {review.send_back_reason && (
                      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                        Send-back reason: {review.send_back_reason}
                      </p>
                    )}
                    {review.decline_reason && (
                      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                        Decline reason: {review.decline_reason}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── APPROVALS TAB ── */}
      {activeTab === 'approvals' && (
        <div className="card">
          <div className="card-header"><h2>IIC Approval Timeline</h2></div>
          <div className="card-body">
            {approvals.length === 0 ? (
              <p className="text-muted" style={{ fontSize: 13 }}>IIC approval has not started yet.</p>
            ) : approvals.map(app => (
              <div key={app.id} style={{
                display: 'flex',
                gap: 14,
                padding: '12px 0',
                borderBottom: '1px solid var(--color-border-light)',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: app.status === 'approved' ? 'var(--color-success-bg)'
                    : app.status === 'pending' ? 'var(--color-warning-bg)'
                    : 'var(--color-error-bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                }}>
                  {app.status === 'approved' ? '✓' : app.status === 'pending' ? '…' : '✗'}
                </div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600 }}>Stage {app.stage_order}: {app.approver_role?.replace(/_/g, ' ')}</p>
                  <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Status: <strong>{app.status?.replace(/_/g, ' ')}</strong>
                    {app.decided_at && ` · Decided ${formatDate(app.decided_at)}`}
                    {app.requested_at && ` · Requested ${formatDate(app.requested_at)}`}
                  </p>
                  {app.notes && <p style={{ fontSize: 13, marginTop: 4, color: 'var(--color-text)' }}>{app.notes}</p>}
                  {app.info_request_details && (
                    <p style={{ fontSize: 13, marginTop: 4, color: 'var(--color-warning)' }}>
                      Info requested: {app.info_request_details}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── DSQ TAB ── */}
      {activeTab === 'dsq' && (
        <div>
          <div className="card mb-4">
            <div className="card-header">
              <h2>DSQ Status</h2>
              {deal.dsq_score != null && (
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-accent)' }}>
                  Score: {deal.dsq_score?.toFixed(2)} / 10
                </span>
              )}
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {[
                  ['Initiated', formatDate(deal.dsq_initiated_at)],
                  ['Initiated By', deal.dsq_initiated_by_name || '—'],
                  ['Company Status', deal.dsq_company_submitted ? 'Submitted' : 'Pending'],
                  ['Scored At', deal.dsq_scored_at ? formatDate(deal.dsq_scored_at) : '—'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</p>
                    <p style={{ fontSize: 14 }}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {dsqResponses.length === 0 ? (
            <div className="alert alert-info">No DSQ responses submitted yet.</div>
          ) : (
            dsqResponses.map(responseDoc => {
              const section = findDsqSection(responseDoc.section_id);
              return (
              <div key={responseDoc.id} className="card mb-4">
                <div className="card-header">
                  <h2>{section?.title || responseDoc.section_id}</h2>
                  <span className="badge badge-default">{responseDoc.visibility || 'shared'}</span>
                </div>
                <div className="card-body">
                  {(responseDoc.responses || []).map((r, idx) => {
                    const question = findDsqQuestion(responseDoc.section_id, r.question_id);
                    const option = r.selected_option_id
                      ? (question?.options || []).find(o => o.option_id === r.selected_option_id)
                      : null;
                    return (
                      <div key={r.question_id || idx} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--color-border-light)' }}>
                        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{question?.question_text || r.question_id}</p>
                        <p style={{ fontSize: 13 }}>{option?.option_text || r.response_value || r.selected_option_id || '—'}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      {/* ── DOCUMENTS TAB ── */}
      {activeTab === 'documents' && (
        <DataroomPanel
          deal={deal}
          dealId={dealId}
          userRole={dealUserRoles[0] || null}
        />
      )}

      {/* ── FRAMEWORKS TAB — internal team only ── */}
      {activeTab === 'frameworks' && isInternalUser && (
        <FrameworksPanel dealId={dealId} />
      )}

      {/* ── e-CIM TAB — internal team only ── */}
      {activeTab === 'ecim' && isInternalUser && (
        <ECIMPanel deal={deal} dealId={dealId} userRole={dealUserRoles[0] || null} />
      )}

      {/* ── TERM SHEET TAB — internal team only ── */}
      {activeTab === 'termsheet' && isInternalUser && (
        <TermSheetPanel deal={deal} dealId={dealId} userRole={dealUserRoles[0] || null} />
      )}

      {/* ── AUDIT LOG TAB ── */}
      {activeTab === 'audit' && (
        <div className="card">
          <div className="card-header"><h2>Audit Log</h2></div>
          <div className="card-body">
            <AuditLogPanel entries={auditLog} />
          </div>
        </div>
      )}

      {/* Reopen Deal (from suspension) Modal */}
      <Modal
        isOpen={dealReopenConfirm}
        onClose={() => setDealReopenConfirm(false)}
        title="Reopen This Deal?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDealReopenConfirm(false)}>Cancel</Button>
            <Button variant="primary" loading={dealReopenLoading} onClick={handleReopenDeal}>Confirm Reopen</Button>
          </>
        }
      >
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          Reopen this deal? It will return to Analyst Review stage for reassessment.
        </p>
      </Modal>

      {/* Reopen Modal */}
      <Modal
        isOpen={reopenModal}
        onClose={() => setReopenModal(false)}
        title="Reopen PIF for Updates"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReopenModal(false)}>Cancel</Button>
            <Button variant="primary" loading={reopenLoading} onClick={handleReopen}>Send Reopen Link</Button>
          </>
        }
      >
        {reopenSuccess ? (
          <div className="alert alert-success">Reopen link sent to the company successfully.</div>
        ) : (
          <div>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
              A secure link will be emailed to the company allowing them to update their application.
              The link expires in 7 days.
            </p>
            <p style={{ fontSize: 13, color: 'var(--color-warning, #92400e)', marginBottom: 12 }}>
              ⚠ Sending a new link invalidates any reopen link previously emailed for this deal.
            </p>
            <div className="form-field">
              <label className="form-label">Reason for Reopening <span className="required">*</span></label>
              <textarea
                className="form-textarea"
                value={reopenReason}
                onChange={e => setReopenReason(e.target.value)}
                placeholder="e.g. Additional financial information required…"
                rows={3}
              />
            </div>
          </div>
        )}
      </Modal>
    </PortalLayout>
  );
}
